"use client"

import { motion } from "framer-motion"
import {
  X,
  Gift,
  QrCode,
  Receipt,
  BellRing,
  BarChart3,
  Check,
  ExternalLink,
  Phone,
} from "lucide-react"

interface SyncmenuPromoModalProps {
  isOpen: boolean
  onClose: () => void
}

const SITE_URL = "https://www.syncmenu.ao"
const PHONE_DISPLAY = "+244 946 808 054"
const PHONE_TEL = "tel:+244946808054"

// Capa: prato típico angolano (comifa) - mesma fonte de imagens do menu
const COMIFA_COVER =
  "https://images.unsplash.com/photo-1611712142269-12b7433e28e9?q=80&w=1935&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"

const FEATURES = [
  {
    icon: QrCode,
    title: "Menu digital automático",
    description: "Cardápio por QR Code, sempre atualizado.",
  },
  {
    icon: Receipt,
    title: "Software de faturação",
    description: "Faturas e recibos integrados ao menu.",
  },
  {
    icon: BellRing,
    title: "Pedidos em tempo real",
    description: "Do cliente direto para a cozinha.",
  },
  {
    icon: BarChart3,
    title: "Gestão & relatórios",
    description: "Vendas e pratos mais pedidos.",
  },
]

const PLANS = [
  {
    name: "Restaurantes",
    price: "10.000 Kz",
    highlight: false,
    items: [
      "Menu digital automático por QR Code",
      "Software de faturação",
      "Pedidos em tempo real",
      "Estoque e inventário",
    ],
  },
  {
    name: "Hotéis",
    price: "70.000 Kz",
    highlight: true,
    items: [
      "Site para reservas",
      "Controle de quartos",
      "Estoque e inventário",
      "1 remetente de SMS marketing com o nome do Hotel",
    ],
  },
]

export default function SyncmenuPromoModal({ isOpen, onClose }: SyncmenuPromoModalProps) {
  if (!isOpen) return null

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[80] bg-background text-foreground overflow-y-auto overscroll-contain"
      role="dialog"
      aria-modal="true"
      aria-label="Promoção Syncmenu"
    >
      <div className="min-h-full flex flex-col">
        {/* ===== Capa ===== */}
        <div className="relative h-44 sm:h-56 shrink-0">
          <img
            src={COMIFA_COVER}
            alt="Comifa - prato típico angolano"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <button
            onClick={onClose}
            aria-label="Fechar"
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm text-white grid place-items-center transition-colors z-30"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="absolute top-4 left-0 right-0 flex flex-col items-center gap-2 z-10 pointer-events-none">
            {/* Logotipo Syncmenu centrado */}
            <img src="/syncmenu-logo.svg" alt="Syncmenu" className="h-10 w-auto rounded-md shadow-md bg-white" />
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-hotel-gold text-hotel-dark text-xs font-bold shadow-md">
              <Gift className="w-3 h-3" />
              7 dias grátis
            </span>
          </div>
        </div>

        {/* ===== Conteúdo ===== */}
        <div className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-5 space-y-5">
          {/* Título */}
          <div>
            <p className="text-hotel-gold-dark text-xs font-semibold uppercase tracking-wide mb-1">
              Menu digital &amp; faturação
            </p>
            <h2 className="text-xl sm:text-2xl font-bold text-foreground leading-tight">
              O futuro do seu restaurante começa aqui
            </h2>
          </div>

          {/* Planos */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2.5">
              Planos mensais · ambos com 7 dias grátis
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {PLANS.map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-lg p-4 bg-card border ${
                    plan.highlight ? "border-hotel-gold/60" : "border-border"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm text-foreground">{plan.name}</h3>
                    {plan.highlight && (
                      <span className="text-[10px] font-bold uppercase tracking-wide text-hotel-gold-dark bg-hotel-gold-light px-1.5 py-0.5 rounded">
                        Completo
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold text-foreground whitespace-nowrap">
                      {plan.price}
                    </span>
                    <span className="text-xs text-muted-foreground">/mês</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">após os 7 dias grátis</p>

                  <ul className="mt-3 space-y-1.5 border-t border-border pt-3">
                    {plan.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2 text-xs text-foreground leading-relaxed"
                      >
                        <Check className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Funcionalidades */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
            {FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="border border-border rounded-lg p-3 bg-card hover:border-primary/40 transition-colors"
              >
                <feature.icon className="w-5 h-5 text-primary mb-2" />
                <h4 className="font-medium text-sm text-foreground leading-snug">{feature.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Ação */}
          <div className="pb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <a
                href={PHONE_TEL}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-border hover:bg-muted text-foreground text-xs font-bold rounded-md transition-colors"
              >
                <Phone className="w-3.5 h-3.5" />
                Ligar agora
              </a>
              <a
                href={SITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-bold rounded-md transition-colors"
              >
                Abrir www.syncmenu.ao
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <p className="text-center text-xs text-muted-foreground mt-2">
              {PHONE_DISPLAY} · Sem cartão de crédito. Cancele quando quiser.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
